import { Box, Container, IconButton, InputAdornment, Link, TextField, Typography, Paper, Fade } from "@mui/material";
import React, { FC, useEffect, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import "./login.css";
import { ReactComponent as GGConnectLogo } from "../../assets/logos/gg-connect-logo-new.svg";
import { ReactComponent as GGConnectLoginImage } from "../../assets/images/gg-connect-login.svg";
import { useAppDispatch, useAppSelector } from "../../redux";
import { loginUser } from "../../redux/reducers/auth.reducer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { queriesApi } from "../../redux/reducers/queries.reducer";
import { resetAllFilters } from "../../redux/reducers/filters.reducer";
import ForgotPasswordDialog from "./ForgotPasswordDialog";
import { SI_ACCESSIBLE_PAGES, USER_ROLES_E } from "../../utils/constants";
import Captcha, { ICaptchaRef } from "./Captcha";

const Login: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const authState = useAppSelector((state) => state.auth);

	const [searchParams] = useSearchParams();

	const captchaRef = useRef<ICaptchaRef>(null);

	const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm();

	function handleSubmitForm(data: FieldValues) {
		if (captchaRef.current && captchaRef.current.checkCaptcha()) {
			dispatch(loginUser({ username: data.username, password: data.password }));
		}
	}

	function handleOpenForgotPasswordDialog() {
		setShowForgotPasswordDialog(true);
	}

	function handleCloseForgotPasswordDialog() {
		setShowForgotPasswordDialog(false);
	}

	function handleToggleShowPassword() {
		setShowPassword(!showPassword);
	}

	useEffect(() => {
		reset({
			username: "",
			password: ""
		});
	}, [reset]);

	useEffect(() => {
		if (authState.user.token && authState.user.token !== "invalid") {
			if (searchParams.has("path")) {
				let path = searchParams.get("path") ?? "";
				path = `${path}?`;

				searchParams.forEach((value, key) => {
					if (key !== "path") {
						path = `${path}${key}=${value}&`;
					}
				});

				if (authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR) {
					if (SI_ACCESSIBLE_PAGES.some((item) => path.startsWith(item))) {
						navigate(path);
					} else {
						navigate("/event-logs");
					}
				} else {
					if (authState.user.hide_data_management && path.startsWith("/data-management")) {
						navigate("/dashboard");
					} else {
						navigate(path);
					}
				}
			} else {
				if (authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR) {
					navigate("/event-logs");
				} else {
					navigate("/dashboard");
				}
			}
		} else {
			dispatch(queriesApi.util.resetApiState());
			dispatch(resetAllFilters());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState.user.token, navigate]);

	useEffect(() => {
		if (authState.currentAction === "auth/loginUser" && authState.error && captchaRef.current) {
			captchaRef.current.resetCaptcha();
		}
	}, [authState.currentAction, authState.error]);

	return (
		<Box className="login-component-wrapper">
			<Container maxWidth="xl" sx={{ height: "100%" }}>
				<Box className="login-content-wrapper">
					<Box className="login-image-section">
						<GGConnectLogo width="85%" />

						<GGConnectLoginImage width="90%" id="connect-login-image" />
					</Box>

					<Box className="login-form-section">
						<Fade in timeout={800}>
							<Paper elevation={0} classes={{ root: "login-form-section-paper" }}>
								<Box className="login-form-section-header">
									<Box className="login-form-header-icon-wrapper">
										<LockOutlinedIcon sx={{ color: "white", fontSize: { xs: 24, sm: 28, md: 32 } }} />
									</Box>

									<Typography variant="h4" color="primary" marginBottom="0.5rem">
										Welcome Back
									</Typography>

									<Typography color="text.secondary" fontWeight={500}>
										Enter your credentials to access your account
									</Typography>
								</Box>

								<Box
									component="form"
									noValidate
									onSubmit={handleSubmit(handleSubmitForm)}
									className="login-form-component"
								>
									<Box>
										<Typography fontWeight={600} color="text.primary" marginBottom="0.5rem">
											Username
										</Typography>

										<TextField
											required
											fullWidth
											autoFocus
											variant="outlined"
											placeholder="Enter your username"
											autoComplete="off"
											error={!!errors.username}
											helperText={errors.username && errors.username.message ? errors.username.message.toString() : ""}
											{...register("username", { required: "Username is required" })}
										/>
									</Box>

									<Box>
										<Typography fontWeight={600} color="text.primary" marginBottom="0.5rem">
											Password
										</Typography>

										<TextField
											required
											fullWidth
											variant="outlined"
											placeholder="Enter your password"
											type={showPassword ? "text" : "password"}
											autoComplete="off"
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<IconButton size="small" onClick={handleToggleShowPassword} tabIndex={-1}>
															{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
														</IconButton>
													</InputAdornment>
												)
											}}
											error={!!errors.password}
											helperText={errors.password && errors.password.message ? errors.password.message.toString() : ""}
											{...register("password", { required: "Password is required" })}
										/>
									</Box>

									<Box sx={{ mb: { xs: 1, sm: 0 } }}>
										<Captcha ref={captchaRef} />
									</Box>

									{authState.currentAction === "auth/loginUser" && authState.error ? (
										<Typography variant="body1" textAlign="center" color="error">
											{authState.error}
										</Typography>
									) : null}

									<LoadingButton
										disableElevation
										variant="contained"
										color="primary"
										sx={{ py: { xs: "0.625rem", sm: "0.75rem" } }}
										type="submit"
										loading={authState.currentAction === "auth/loginUser" && authState.loading}
									>
										Sign In
									</LoadingButton>

									<Box sx={{ textAlign: "center" }}>
										<Link
											onClick={handleOpenForgotPasswordDialog}
											fontWeight={500}
											sx={{
												textDecoration: "none",
												"&:hover": {
													textDecoration: "underline"
												}
											}}
										>
											Forgot your password?
										</Link>
									</Box>
								</Box>
							</Paper>
						</Fade>
					</Box>
				</Box>
			</Container>

			<ForgotPasswordDialog open={showForgotPasswordDialog} onClose={handleCloseForgotPasswordDialog} />
		</Box>
	);
};

export default Login;
